import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useToast } from "@shared/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/ui/dialog";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { useCreateResourceSubType, useEditResourceTypeSubtype, useMeterReadingFormat } from "../../../../hooks";
import { useGetResourceTypeList } from "../../../../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

interface ResourceSubtypeFormProps {
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  editData?: any;
  onSuccess?: (newData: any) => void;
}

export function ResourceSubtypeForm({
  trigger,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  editData,
}: ResourceSubtypeFormProps) {
  const { toast } = useToast();
  const [localOpen, setLocalOpen] = useState(false);
  const createMutation = useCreateResourceSubType();
  const editMutation = useEditResourceTypeSubtype();
  const createResourceSubType = createMutation.mutate;
  const editResourceSubType = editMutation.mutate;
  const isLoading = Boolean(createMutation.isPending || editMutation.isPending);

  const remoteUtilityId = useRemoteUtilityId();
  const { data: meterReadingFormats } = useMeterReadingFormat(remoteUtilityId, "");
  const { data: resourceTypesData } = useGetResourceTypeList(remoteUtilityId);

  const resourceTypeOptions = React.useMemo(() => {
    const list = resourceTypesData?.result || [];
    return list.map((item: any) => ({
      label: item.name,
      value: item.code,
    }));
  }, [resourceTypesData]);

  const readFormatOptions = React.useMemo(() => {
    return Array.isArray(meterReadingFormats)
      ? meterReadingFormats.map((m: any) => ({
          label: m.name,
          value: m.id,
        }))
      : [];
  }, [meterReadingFormats]);

  const open = controlledOpen !== undefined ? controlledOpen : localOpen;
  const setOpen = controlledOnOpenChange !== undefined ? controlledOnOpenChange : setLocalOpen;
  const isEditMode = Boolean(editData);

  const getInitialValues = () => {
    if (!editData) {
      return {
        name: "",
        code: "",
        resourceType: "",
        readFormat: "",
        description: "",
        customFields: [],
      };
    }

    // Prefill customFields if present, otherwise default to one empty row
    const apiFields = editData.customFields || editData.custom_fields || [];
    const customFieldsRaw = Array.isArray(apiFields) && apiFields.length > 0
      ? apiFields.map((field: any) => ({
          label: field.label || field.fieldLabel || field.field_label || "",
          type: (field.type || "text").toLowerCase(),
          placeholder: field.placeholder || "",
          required: field.required !== undefined 
            ? field.required 
            : (field.isRequired !== undefined 
                ? field.isRequired 
                : (field.is_required !== undefined ? field.is_required : false)),
        }))
      : (editData.fields && editData.fields !== "None"
          ? editData.fields.split(",").map((field: string) => ({
              label: field.trim(),
              type: "text",
              placeholder: "",
              required: false,
            }))
          : []
        );

    return {
      name: editData.name || "",
      code: editData.code || "",
      resourceType: editData.resourceType || editData.resource_type || "",
      readFormat: editData.readFormat || editData.read_format || "",
      description: editData.description || "",
      customFields: customFieldsRaw,
    };
  };

  const formFields: FormField[] = [
    // Basic Info Group
    {
      name: "name",
      type: "text",
      label: "Name",
      placeholder: "Enter sub type name...",
      required: true,
      group: "Basic info",
      groupColorNeeded: false,
      groupHelperText: "Configure basic settings and identifiers of this resource sub type.",
    },
    {
      name: "code",
      type: "text",
      label: "Code",
      placeholder: "Enter code...",
      required: true,
      group: "Basic info",
    },
    {
      name: "resourceType",
      type: "select",
      label: "Resource Type",
      placeholder: "Select resource type...",
      required: true,
      group: "Basic info",
      className: "md:col-span-2 mt-4",
      options: resourceTypeOptions,
    },
    {
      name: "readFormat",
      type: "select",
      label: "Read Format",
      placeholder: "Select a read format",
      helperText: "The read format that will be used when collecting readings for this sub type.",
      required: true,
      group: "Basic info",
      className: "md:col-span-2 mt-4",
      options: readFormatOptions,
      showWhen: (formValues) => {
        const selectedType = (resourceTypesData?.result || []).find(
          (item: any) => item.code === formValues?.resourceType
        );
        return selectedType?.capabilities?.captureReads === 1;
      },
    },
    {
      name: "description",
      type: "textarea",
      label: "Description",
      placeholder: "Enter description...",
      rows: 2,
      group: "Basic info",
      className: "md:col-span-2 mt-4",
    },
    // Custom Fields Group
    {
      name: "customFields",
      type: "form-array",
      group: "Custom Fields",
      groupHelperText: "These fields will be required when this sub type is selected.",
      groupColorNeeded: false,
      addButtonText: "Add field",
      emptyMessage: "No custom fields added yet",
      className: "md:col-span-2 [&_.flex-1.grid]:lg:grid-cols-4",
      minItems: 1,
      itemFields: [
        {
          name: "label",
          type: "text",
          label: "Field Label",
          placeholder: "e.g. Serial Number",
          required: true,
        },
        {
          name: "type",
          type: "select",
          label: "Type",
          required: true,
          defaultValue: "text",
          options: [
            { label: "Text", value: "text" },
            { label: "Number", value: "number" },
            { label: "Date", value: "date" },
            { label: "Yes/No", value: "radio" },
            { label: "Dropdown", value: "select" },
          ],
        },
        {
          name: "placeholder",
          type: "text",
          label: "Placeholder",
          placeholder: "e.g. Enter value...",
        },
        {
          name: "required",
          type: "switch",
          label: "Required",
          defaultValue: false,
        },
      ],
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    defaultValues: getInitialValues(),
  });

  // Reset form when dialog opens/closes or editData changes
  useEffect(() => {
    if (open) {
      form.reset(getInitialValues());
    }
  }, [open, editData]);

  const handleSubmit = (data: any) => {
    if (!data.name.trim() || !data.code.trim() || !data.resourceType) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    const fieldLabels = data.customFields
      ? data.customFields
          .map((f: any) => f.label)
          .filter(Boolean)
          .join(", ")
      : "";

    const payload: any = {
      name: data.name,
      code: `${data.resourceType}_${data.name}`,
      configuration_code: data.code,
      remote_utility_id: remoteUtilityId,
      resource_type: data.resourceType,
      description: data.description,
      custom_fields: Array.isArray(data.customFields)
        ? data.customFields.map((f: any) => ({
            field_label: f.label,
            field_key: f.label ? f.label.replace(/\s+/g, "_").toLowerCase() : "",
            type: f.type ? f.type.charAt(0).toUpperCase() + f.type.slice(1) : "Text",
            placeholder: f.placeholder || "",
            is_required: Boolean(f.required),
          }))
        : [],
    };

    if (data.readFormat) {
      payload.read_format = data.readFormat;
    }

    if (isEditMode) {
       payload.code = editData?.code;
       editResourceSubType(payload, {
        onSuccess: (response) => {
          setOpen(false);
        },
      });
    } else {
      createResourceSubType(payload, {
        onSuccess: (response) => {
          setOpen(false);
        },
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? "Edit resource sub type" : "Add resource sub type"}</DialogTitle>
        </DialogHeader>

        <DynamicForm
          fields={formFields}
          form={form}
          onSubmit={handleSubmit}
          onReset={() => setOpen(false)}
          loading={isLoading}
          gridColumns={2}
          responsiveColumns={{
            sm: 1,
            md: 2,
          }}
          config={{
            showResetButton: true,
            showSubmitButton: true,
            submitButtonText: isEditMode ? "Update sub type" : "Add sub type",
            resetButtonText: "Cancel",
            classes: FIELD_CLASSES,
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
